export interface Coordinate {
  latitude: number;
  longitude: number;
  location_time: string | Date;
}

export type Segment = {
  type: 'break' | 'work';
  coordinates: Coordinate[] | [Coordinate, Coordinate];
  index: number;
};

// Combine geo_locations with optional start and end coordinates
export function getFullCoordinatesList(
  geo_locations: Coordinate[],
  start_lat?: number,
  start_lng?: number,
  clock_in_time?: string,
  end_lat?: number,
  end_lng?: number,
  clock_out_time?: string
): Coordinate[] {
  const coordinates = [...(geo_locations || [])];

  if (start_lat && start_lng) {
    coordinates.unshift({
      latitude: start_lat,
      longitude: start_lng,
      location_time: clock_in_time || '',
    });
  }

  if (end_lat && end_lng) {
    coordinates.push({
      latitude: end_lat,
      longitude: end_lng,
      location_time: clock_out_time || '',
    });
  }

  return coordinates;
}

// Round date to nearest 5 seconds
function roundToNearestFiveSeconds(date: Date): Date {
  const ms = 1000 * 5; // 5 seconds in milliseconds
  return new Date(Math.round(date.getTime() / ms) * ms);
}

// Split coordinates into break segments and work segments
export function getTimelineSegments(coordinates: Coordinate[], breaks: Array<any>): Array<Segment> {
  const segments: Array<Segment> = [];

  let currentWorkSegment: Coordinate[] = [];
  let workIndex = 0;
  let breakIndex = 0;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const current = coordinates[i];
    const next = coordinates[i + 1];

    if (!current?.location_time || !next?.location_time) continue;

    const currentTime = new Date(current.location_time);
    const nextTime = new Date(next.location_time);

    if (isNaN(currentTime.getTime()) || isNaN(nextTime.getTime())) continue;

    currentWorkSegment.push(current);

    const roundedCurrentTime = roundToNearestFiveSeconds(currentTime);
    const roundedNextTime = roundToNearestFiveSeconds(nextTime);

    // Round break start/end times and current/next times to the nearest 5 seconds
    // to allow break detection with a 5-second fault tolerance.
    const isBreak = breaks.some(({ startLocal, endLocal }) => {
      if (!startLocal) return false;

      const roundedBreakStart = roundToNearestFiveSeconds(new Date(startLocal));

      // Handle ongoing breaks (no end time)
      if (!endLocal) return roundedBreakStart < roundedNextTime;

      const roundedBreakEnd = roundToNearestFiveSeconds(new Date(endLocal));

      // Check for any overlap between break and current time window
      return roundedBreakStart < roundedNextTime && roundedBreakEnd > roundedCurrentTime;
    });

    if (isBreak) {
      // Finish current work segment before break
      if (currentWorkSegment.length > 0) {
        segments.push({
          type: 'work',
          coordinates: [...currentWorkSegment],
          index: workIndex++,
        });
        currentWorkSegment = [];
      }

      // Add break segment
      segments.push({
        type: 'break',
        coordinates: [current, next],
        index: breakIndex++,
      });
    }
  }

  // Push the last point into work segment
  const last = coordinates[coordinates.length - 1];
  if (last) {
    currentWorkSegment.push(last);
  }

  if (currentWorkSegment.length > 0) {
    segments.push({
      type: 'work',
      coordinates: currentWorkSegment,
      index: workIndex++,
    });
  }

  return segments;
}
